import React from 'react';

const AwningsProjects: React.FC = () => {
  return (
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-primary mb-4">Recent Awning Installations</h2>
          <p className="text-xl text-gray-600">See our cooling solutions in action across Ontario</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="h-48 overflow-hidden">
              <img 
                src="images/awnings/garden-folding-arm-awnings629a0.webp" 
                alt="Smart Shade awning installation on Mississauga patio" 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold text-primary mb-2">Mississauga Patio</h3>
              <p className="text-gray-600 mb-3">
                16×10 Evie Vista with solid burgundy fabric. Covers dining area perfectly.
              </p>
              <div className="flex justify-between items-center">
                <span className="text-sm text-accent font-semibold">Installed: July 2024</span>
                <span className="text-sm text-gray-500">$5,495</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="h-48 overflow-hidden">
              <img 
                src="images/awnings/vertical drop awning.webp" 
                alt="Quick Shade vertical drop awning on Toronto condo" 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold text-primary mb-2">Toronto Condo</h3>
              <p className="text-gray-600 mb-3">
                12×8 Evie Breeze for privacy and sun protection. Perfect for condos.
              </p>
              <div className="flex justify-between items-center">
                <span className="text-sm text-accent font-semibold">Installed: June 2024</span>
                <span className="text-sm text-gray-500">$4,295</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="h-48 overflow-hidden">
              <img 
                src="images/awnings/strong-open-retractable-awning21d8530b-0612-4a5e-a5f9-ce29f7156471.webp" 
                alt="Complete Shade premium full-cassette awning on Burlington deck" 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="p-6">
              <h3 className="text-xl font-semibold text-primary mb-2">Burlington Deck</h3>
              <p className="text-gray-600 mb-3">
                20×12 Evie Horizon with smart controls. Restaurant-quality at home.
              </p>
              <div className="flex justify-between items-center">
                <span className="text-sm text-accent font-semibold">Installed: August 2024</span>
                <span className="text-sm text-gray-500">$7,995</span>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Testimonials */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-primary text-center mb-12">What Our Customers Say</h3>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">👩‍💼</span>
                <div className="text-yellow-400">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "Our Smart Shade awning has been a game-changer! The patio is now 12 degrees cooler, 
                and our AC bills have dropped significantly. The smart home integration works perfectly."
              </p>
              <p className="text-primary font-semibold">— Jennifer K., Mississauga</p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">👨‍👩‍👧‍👦</span>
                <div className="text-yellow-400">★★★★★</div>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "The vertical drop awning gives us perfect privacy on our condo balcony. 
                Installation was quick and professional. Highly recommend!"
              </p>
              <p className="text-primary font-semibold">— The Chen Family, Toronto</p>
            </div>
          </div>
        </div>

        {/* Installation Process */}
        <div className="mt-16">
          <h3 className="text-2xl font-bold text-primary text-center mb-12">Quick Installation Process</h3>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-accent text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                1
              </div>
              <h4 className="text-lg font-semibold text-primary mb-3">Site Assessment</h4>
              <p className="text-gray-600">
                We measure your space and assess electrical requirements for optimal placement.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-accent text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                2
              </div>
              <h4 className="text-lg font-semibold text-primary mb-3">Custom Fabrication</h4>
              <p className="text-gray-600">
                Your awning is custom-made to exact specifications with premium fabrics.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-accent text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                3
              </div>
              <h4 className="text-lg font-semibold text-primary mb-3">Professional Installation</h4>
              <p className="text-gray-600">
                CSA-certified installation with electrical work completed in 4-6 hours.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-accent text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-6 text-xl font-bold">
                4
              </div>
              <h4 className="text-lg font-semibold text-primary mb-3">Smart Setup</h4>
              <p className="text-gray-600">
                We configure smart home integration and train you on all features.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-accent to-green-700 rounded-xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Cool Your Space?</h3>
            <p className="text-xl text-green-100 mb-6">
              Start saving on energy costs with a custom awning solution
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/awning-quote"
                className="bg-white text-accent px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
              >
                Get Awning Quote
              </a>
              <a
                href="#awnings"
                className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-accent transition-colors inline-block"
              >
                Compare Models
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AwningsProjects;
